import React, { useState, useRef, useEffect } from "react";
import { Square, CheckSquare, Search as SearchIcon } from "lucide-react";
// Assuming a Back icon component or SVG is available for the reset button

const MultiSelectDropdown = ({
  options = [],
  selectedOptions = [],
  onSelectedChange = () => {}, // Called on every individual item toggle
  onApply = () => {}, // Called when "Ok" is clicked to confirm selection
  placeholder = "Select options",
  columnId,
  onClose, // Now optional, will default to closing the dropdown element
  resetText = "Сбросить",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [localSelected, setLocalSelected] = useState([]);
  const dropdownRef = useRef(null);
  const searchBarRef = useRef(null);

  // Default close behavior: remove the dropdown from the DOM if no onClose is provided
  const defaultClose = () => {
    if (dropdownRef.current) {
      dropdownRef.current.style.display = "none";
    }
  };

  // Use provided onClose or fallback to defaultClose
  const handleClose = typeof onClose === "function" ? onClose : defaultClose;

  useEffect(() => {
    // Defensive: ensure localSelected is always an array
    setLocalSelected(Array.isArray(selectedOptions) ? selectedOptions : []);
  }, [selectedOptions]);

  const getOptionLabel = (option) => {
    if (typeof option === "object" && option !== null) {
      return option.name || option.label || String(option);
    }
    return String(option);
  };

  const getOptionId = (option) => {
    if (typeof option === "object" && option !== null) {
      return option.id || option.value || String(option); // Ensure string for comparison if needed
    }
    return String(option);
  };

  const isOptionSelected = (option) => {
    const optionId = getOptionId(option);
    // Defensive: ensure localSelected is always an array
    return (
      Array.isArray(localSelected) &&
      localSelected.some((item) => getOptionId(item) === optionId)
    );
  };


  useEffect(() => {
    setFilteredOptions(
      options.filter((option) =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, options]);

  const toggleOption = (option) => {
    const isSelected = isOptionSelected(option);
    const optionId = getOptionId(option);
    let newSelection;
    if (isSelected) {
      newSelection = localSelected.filter(
        (item) => getOptionId(item) !== optionId
      );
    } else {
      newSelection = [...localSelected, option];
    }
    setLocalSelected(newSelection);
    onSelectedChange(newSelection, columnId); // Notify parent of intermediate change
  };

  const handleApplyInternal = () => {
    onApply(localSelected, columnId);
    handleClose();
  };

  const handleResetInternal = () => {
    setLocalSelected([]);
    onSelectedChange([], columnId); // Notify parent of intermediate reset
  };

  const handleCancel = () => {
    setLocalSelected(Array.isArray(selectedOptions) ? selectedOptions : []); // Revert to original prop
    onSelectedChange(
      Array.isArray(selectedOptions) ? selectedOptions : [],
      columnId
    ); // Notify parent of revert
    handleClose();
  };

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  return (
    <div ref={dropdownRef} className="w-full">
      <div className="w-full p-3 bg-[#EAEFF3] rounded-md max-h-[calc(60px+200px+40px)] overflow-y-auto">
        <div
          ref={searchBarRef}
          onClick={(e) => e.stopPropagation()}
          className="mb-2"
        >
          <div className="flex items-center p-1 border border-gray-300 rounded-md bg-white">
            <SearchIcon size={16} className="text-gray-400 mx-2" />
            <input
              type="text"
              placeholder="Search"
              className="w-full text-sm focus:outline-none bg-transparent py-1"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        <div className=" my-2">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isChecked = isOptionSelected(option);
              const optionKey = getOptionId(option) || index;
              return (
                <label
                  key={optionKey}
                  className="flex items-center justify-between py-1 cursor-pointer rounded hover:bg-gray-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(option);
                  }}
                >
                  <div className="flex items-center p-2">
                    {isChecked ? (
                      <CheckSquare
                        className="rounded-md mr-2"
                        stroke="#0832DE"
                        width={20}
                        height={20}
                        strokeWidth={1.5}
                      />
                    ) : (
                      <Square
                        className="rounded-md mr-2"
                        stroke="#0832DE"
                        width={20}
                        height={20}
                        strokeWidth={1}
                      />
                    )}
                    <span className="text-gray-700 text-sm">
                      {getOptionLabel(option)}
                    </span>
                  </div>
                </label>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchTerm
                ? `No options found matching your search.`
                : placeholder || "No options available"}
            </div>
          )}
        </div>

        <div className="flex justify-center gap-1 pt-2 border-t border-gray-300">
          <button
            onClick={handleCancel}
            className="flex items-center justify-center px-4 py-2 bg-[#CED5E5] text-[#2B3674] w-32 h-8 text-sm border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-[#B8C5D9]"
          >
            Cancel
          </button>
          <button
            onClick={handleApplyInternal}
            className="flex items-center justify-center px-4 py-2 bg-[#0832DE] text-white w-20 h-8 text-sm border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-[#0625B8]"
          >
            Ok
          </button>
        </div>
      </div>

      <button
        onClick={handleResetInternal}
        className="flex items-center justify-start w-full mt-2 px-4 py-2 bg-white text-[#2B3674] text-base border-none rounded-lg cursor-pointer transition-colors duration-200 ease-in-out focus:outline-none hover:bg-gray-50 gap-1"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-4 h-4"
        >
          <path
            d="M13.878 7.45603C13.7209 6.68827 13.4139 5.95902 12.9747 5.31003C12.5431 4.67155 11.9931 4.12165 11.3547 3.69003C10.7056 3.25092 9.97638 2.94396 9.20867 2.7867C8.8054 2.70492 8.39481 2.66471 7.98333 2.6667V1.33203L5.33333 3.33203L7.98333 5.33203V4.00003C8.306 3.9987 8.62867 4.02936 8.94 4.09336C9.53668 4.21564 10.1035 4.45417 10.608 4.79536C11.1056 5.13086 11.5338 5.55911 11.8693 6.0567C12.3902 6.82685 12.668 7.7356 12.6667 8.66536C12.6666 9.28929 12.5419 9.90691 12.3 10.482C12.1824 10.7591 12.0386 11.0243 11.8707 11.274C11.7021 11.5223 11.5106 11.7542 11.2987 11.9667C10.6533 12.6108 9.83407 13.0527 8.94133 13.238C8.32048 13.3634 7.68085 13.3634 7.06 13.238C6.46304 13.1156 5.89603 12.8769 5.39133 12.5354C4.89432 12.2002 4.46653 11.7724 4.13133 11.2754C3.61104 10.5044 3.33314 9.59547 3.33333 8.66536H2C2.00071 9.86116 2.35792 11.0296 3.026 12.0214C3.4579 12.6588 4.00724 13.2081 4.64467 13.64C5.63508 14.3103 6.80407 14.6676 8 14.6654C8.40618 14.6653 8.81133 14.6245 9.20933 14.5434C9.97649 14.385 10.7053 14.0781 11.3547 13.64C11.6735 13.4252 11.9707 13.1798 12.242 12.9074C12.5137 12.6349 12.7592 12.3376 12.9753 12.0194C13.6451 11.0292 14.0021 9.86075 14 8.66536C14 8.25918 13.9591 7.85404 13.878 7.45603Z"
            fill="#2B3674"
          />
        </svg>
        {resetText}
      </button>
    </div>
  );
};

export default MultiSelectDropdown;
